// Verbund-Aggregation der Kennzahlen: aus den Jahresdaten mehrerer Firmen eine
// konsolidierte Zelle bauen (Summe, unvollständige Summe, Einzelwert-Liste).
//
// Diese Datei ist bewusst REACT-FREI — kein Import aus react, @fluentui oder
// KennzahlenTab.tsx. Sie ist reine Rechenlogik und muss in der node-Test-
// umgebung ohne DOM laufen.

import { defaultAgg, fmtEur, fmtNum, parseEuroStrict } from "./kpiFormat";
import type { Decoded, FieldRowDef, FieldType, RowDef, SectionRowDef } from "./kpiTypes";

/** Anzeige für „nichts erfasst" — identisch zur Einzelfirmen-Ansicht. */
const EMPTY_TEXT = "—";
/** Marker vor einer unvollständigen Summe (mindestens ein Wert war Freitext). */
const MIXED_PREFIX = "≥ ";
/** Ab wie vielen Einzelwerten der Kompakt-Text auf „+n" kürzt. */
const LIST_MAX = 3;

/** Jahresdatensatz einer Firma des Verbunds. */
export interface FirmYearData {
  contactId: number;
  name: string | null;
  data: Decoded;
}

/** Beitrag einer Firma zu einer aggregierten Zelle. */
export interface AggPart {
  contactId: number;
  name: string;
  kuerzel: string;
  /** Rohwert der Firma (String, Zahl oder yesNo-Objekt) — für die gestapelte Anzeige. */
  value: unknown;
  /** Numerisch, sofern eindeutig parsebar; sonst null. */
  num: number | null;
}

export interface AggCell {
  total: number | null;
  parts: AggPart[];
  kind: "sum" | "mixed" | "list" | "empty";
  text: string;
}

/** Type-Guard fürs Vorfiltern von `RowDef[]` (filter() narrowt die Union nicht selbst). */
export const isFieldRow = (r: RowDef): r is FieldRowDef => r.kind === "field";

/**
 * Löst `summaryAggRowId` auf: je Sektion die Feldzeile, deren aggregierte Zelle
 * die Sektionszeile im Verbund anzeigt.
 *
 * Die Sektionszeile bekommt damit KEINE eigene Rechnung, sondern genau die Zelle
 * ihrer Leitzeile — inklusive `agg`, `aggParse` und „≥"-Marker. Rechnete sie
 * selbst, könnten oben und eine Zeile darunter zwei verschiedene Zahlen für
 * dasselbe Feld stehen.
 *
 * Ein ins Leere zeigender Verweis wird STILL übergangen (die Sektion behält
 * ihren Summary-Text) statt zu werfen: der Katalog wird beim Modulstart gebaut,
 * eine Exception hier weißte den kompletten Reiter. Dass jeder Verweis trifft,
 * sichert stattdessen ein Test.
 */
export function resolveSectionAggRows(
  sections: readonly SectionRowDef[],
  fieldRows: readonly FieldRowDef[],
): Map<string, FieldRowDef> {
  const byId = new Map(fieldRows.map((r) => [r.id, r]));
  const out = new Map<string, FieldRowDef>();
  for (const sec of sections) {
    const row = sec.summaryAggRowId ? byId.get(sec.summaryAggRowId) : undefined;
    if (row) out.set(sec.id, row);
  }
  return out;
}

const isYesNo = (type: FieldType): boolean =>
  type === "yesNoText" || type === "yesNoNumText" || type === "yesNoPartner";

/**
 * „Kein Wert" — inklusive der leeren yesNo-Objekte: die `get`-Funktionen dieser
 * Zeilen liefern `{}`, wenn nie etwas erfasst wurde. Ohne diese Prüfung zählte
 * jede Firma als Wert und die Zelle wäre nie "empty".
 */
const isEmptyValue = (v: unknown): boolean => {
  if (v === undefined || v === null || v === "") return true;
  if (typeof v === "object") {
    return Object.values(v as Record<string, unknown>).every(
      (x) => x === undefined || x === null || x === "",
    );
  }
  return false;
};

/**
 * Standard-Parser, wenn die Zeile kein eigenes `aggParse` mitbringt.
 *
 * Für JEDEN Typ strikt (`parseEuroStrict`) — auch für "number". Der laxe
 * `parseNum` liest den deutschen Tausenderpunkt als Dezimalpunkt und machte aus
 * „1.200" die Zahl 1,2; die Zelle meldete trotzdem `kind: "sum"` und damit eine
 * exakt aussehende Summe, die um Faktor 1000 danebenliegt.
 */
const defaultParse = (v: unknown): number | null => {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v !== "string") return null;
  return parseEuroStrict(v);
};

/** Rechtsformen und Füllwörter, die kein Kürzel tragen. */
const RECHTSFORMEN = new Set([
  "gmbh", "mbh", "ag", "kg", "kgaa", "ohg", "gbr", "ug", "se", "ek", "gmbhcokg",
  "co", "cie", "e", "k", "v", "haftungsbeschraenkt", "und", "the",
]);

const splitWords = (name: string): string[] =>
  name.split(/[^\p{L}\p{N}]+/u).filter(Boolean);

/**
 * 2–3-stelliges Kürzel für die gestapelte Verbund-Anzeige (Farbpunkt + Kürzel + Wert).
 * Rechtsformen fallen raus, damit aus „Müller Bau GmbH" nicht „MBG" wird; bleibt
 * nur ein Wort übrig, sind es dessen erste drei Buchstaben.
 */
export function firmenKuerzel(name: string | null | undefined, contactId: number): string {
  const words = splitWords((name ?? "").trim());
  const ohneRechtsform = words.filter((w) => !RECHTSFORMEN.has(w.toLowerCase()));
  // Heißt die Firma nur nach ihrer Rechtsform, ist der gefilterte Rest leer —
  // dann lieber die Original-Wörter als gar kein Kürzel.
  const basis = ohneRechtsform.length > 0 ? ohneRechtsform : words;
  if (basis.length === 0) return `#${contactId}`;
  if (basis.length === 1) {
    const w = basis[0];
    return w.charAt(0).toUpperCase() + w.slice(1, 3).toLowerCase();
  }
  return basis
    .slice(0, 3)
    .map((w) => w.charAt(0).toUpperCase())
    .join("");
}

/** Letztes weggefiltertes Rechtsform-Wort — „Müller Bau GmbH & Co. KG" → „KG". */
const rechtsform = (name: string | null | undefined): string | null => {
  const formen = splitWords((name ?? "").trim()).filter((w) => RECHTSFORMEN.has(w.toLowerCase()));
  return formen.length > 0 ? formen[formen.length - 1] : null;
};

/**
 * Kürzel für ALLE Firmen des Verbunds auf einmal — mit aufgelösten Kollisionen.
 *
 * „Müller Bau GmbH" und „Müller Bau GmbH & Co. KG" ergeben beide „MB", weil
 * `firmenKuerzel` die Rechtsform verwirft. Die Komplementär-GmbH neben der KG
 * ist aber der Normalfall eines Firmenverbunds, nicht der Ausnahmefall: im
 * gestapelten Wert stünden dann zwei Zeilen „MB 40" / „MB 60", die sich nur noch
 * am Farbpunkt unterscheiden. Kollidierende Firmen bekommen deshalb ihre
 * Rechtsform angehängt („MB·GmbH", „MB·KG"); bleibt es auch dann gleich,
 * entscheidet eine laufende Nummer.
 *
 * Gebildet wird über die gesamte Firmenliste, nicht nur über die Firmen mit
 * Wert — sonst trüge dieselbe Firma je nach Zeile ein anderes Kürzel, sobald die
 * Namensgleiche in einer Zeile nichts erfasst hat.
 */
export function buildKuerzelMap(
  firmen: { contactId: number; name: string | null }[],
): Map<number, string> {
  const namen = new Map<number, string | null>();
  const gruppen = new Map<string, number[]>();
  for (const f of firmen) {
    if (namen.has(f.contactId)) continue;
    namen.set(f.contactId, f.name);
    const k = firmenKuerzel(f.name, f.contactId);
    const liste = gruppen.get(k) ?? [];
    liste.push(f.contactId);
    gruppen.set(k, liste);
  }

  const out = new Map<number, string>();
  for (const [k, ids] of gruppen) {
    if (ids.length === 1) {
      out.set(ids[0], k);
      continue;
    }
    const vergeben = new Set<string>();
    ids.forEach((id, i) => {
      const form = rechtsform(namen.get(id));
      let kandidat = form ? `${k}·${form}` : `${k}·${i + 1}`;
      if (vergeben.has(kandidat)) kandidat = `${k}·${i + 1}`;
      vergeben.add(kandidat);
      out.set(id, kandidat);
    });
  }
  return out;
}

/**
 * Zahlformat der Einzelbeträge und Summen einer Zeile.
 *
 * Prozente werden nie summiert (siehe `defaultAgg`), tauchen hier aber als
 * Ring-/Popover-Format nicht auf — die Funktion deckt nur die numerischen Fälle
 * ab; die Einheit hängt `formatPartValue` an.
 */
export const aggNumberFormat = (type: FieldType): ((n: number) => string) =>
  type === "number" ? fmtNum : type === "percent" ? (n) => `${fmtNum(n)} %` : fmtEur;

/**
 * Einzelwert einer Firma als Text — MIT Einheit („6,2 %", „1.000.000 €", …).
 *
 * Einzige Quelle für den Kompakt-Text UND für die gestapelte Anzeige der Zelle:
 * die hatte vorher eine eigene Variante ohne Einheit, in der eine Prozentzeile
 * als nackte „40" in einer Spalte voller Euro-Beträge stand.
 */
export const formatPartValue = (type: FieldType, part: Pick<AggPart, "value" | "num">): string => {
  if (part.value !== null && typeof part.value === "object") {
    const active = (part.value as { active?: boolean | null }).active;
    return active === true ? "ja" : active === false ? "nein" : EMPTY_TEXT;
  }
  if (typeof part.value === "number") return aggNumberFormat(type)(part.value);
  if (type === "percent") return `${String(part.value)} %`;
  if (type === "euro" || type === "number") {
    return part.num !== null ? aggNumberFormat(type)(part.num) : String(part.value);
  }
  return String(part.value);
};

/** yesNo-Zeilen verdichten sich zu einer Auszählung statt zu einer Werteliste. */
const yesNoText = (parts: AggPart[]): string => {
  let ja = 0;
  let nein = 0;
  for (const p of parts) {
    const active = (p.value as { active?: boolean | null } | null)?.active;
    if (active === true) ja += 1;
    else if (active === false) nein += 1;
  }
  const segs: string[] = [];
  if (ja > 0) segs.push(`${ja}× ja`);
  if (nein > 0) segs.push(`${nein}× nein`);
  return segs.length > 0 ? segs.join(" · ") : EMPTY_TEXT;
};

/** Kompakter Fallback-Text, wenn die Zelle die Einzelwerte nicht stapeln kann. */
const listText = (type: FieldType, parts: AggPart[]): string => {
  if (isYesNo(type)) return yesNoText(parts);
  const shown = parts.slice(0, LIST_MAX).map((p) => formatPartValue(type, p));
  const rest = parts.length - shown.length;
  return rest > 0 ? `${shown.join(" · ")} +${rest}` : shown.join(" · ");
};

/** Summen-Text: Stückzahlen bleiben nackte Zahlen (deutsch formatiert), alles andere ist Geld. */
const sumText = (type: FieldType, total: number): string => aggNumberFormat(type)(total);

/**
 * Aggregiert eine Zeile über alle Firmen des Verbunds für ein Jahr.
 *
 * Ob summiert wird, entscheidet `row.agg ?? defaultAgg(row.type)`; geparst wird
 * mit `row.aggParse`, sonst mit dem Standard-Parser des Typs. Firmen ohne Wert
 * tauchen nicht in `parts` auf — sie zählen insbesondere NICHT als 0, sonst
 * verwässerten fehlende Erfassungen jede Quote.
 */
export function aggregateCell(row: FieldRowDef, perFirm: FirmYearData[]): AggCell {
  const agg = row.agg ?? defaultAgg(row.type);
  const parse = row.aggParse ?? defaultParse;
  const kuerzel = buildKuerzelMap(perFirm);

  const parts: AggPart[] = [];
  for (const firma of perFirm) {
    const value = row.get(firma.data);
    if (isEmptyValue(value)) continue;
    parts.push({
      contactId: firma.contactId,
      name: firma.name ?? `#${firma.contactId}`,
      kuerzel: kuerzel.get(firma.contactId) ?? firmenKuerzel(firma.name, firma.contactId),
      value,
      num: parse(value),
    });
  }

  if (parts.length === 0) {
    return { total: null, parts: [], kind: "empty", text: EMPTY_TEXT };
  }

  if (agg !== "sum") {
    // Reihenfolge = Reihenfolge der Firmenliste, damit die Farbzuordnung der
    // gestapelten Einzelwerte über alle Zeilen hinweg stabil bleibt.
    return { total: null, parts, kind: "list", text: listText(row.type, parts) };
  }

  const parsebar = parts.filter((p) => p.num !== null);
  const vollstaendig = parsebar.length === parts.length;
  const total = parsebar.length > 0 ? parsebar.reduce((acc, p) => acc + (p.num as number), 0) : null;

  // Absteigend nach Betrag; nicht parsebare Werte hinten, sie haben keinen Rang.
  parts.sort((a, b) => {
    if (a.num === null && b.num === null) return 0;
    if (a.num === null) return 1;
    if (b.num === null) return -1;
    return b.num - a.num;
  });

  if (vollstaendig && total !== null) {
    return { total, parts, kind: "sum", text: sumText(row.type, total) };
  }
  // Unvollständige Summe: der „≥"-Marker ist Pflicht, sonst wird eine Summe, in
  // der ein „ca. 2 Mio" fehlt, für die vollständige Summe gehalten.
  return {
    total,
    parts,
    kind: "mixed",
    // Ohne einen einzigen parsebaren Wert gibt es keine Summe — dann zeigen wir
    // die Rohwerte; ein „≥ 0 €" wäre schlicht gelogen.
    text: total !== null ? MIXED_PREFIX + sumText(row.type, total) : listText(row.type, parts),
  };
}

/**
 * Synthetisches `Decoded` für ein Jahr aus den Werten aller Verbundfirmen — damit
 * die bestehenden `summary(y)`-Funktionen der Sektionen unverändert weiterlaufen
 * und in der Verbund-Ansicht die konsolidierten Zahlen zeigen.
 */
export function buildConsolidatedDecoded(
  year: number,
  fieldRows: FieldRowDef[],
  perFirm: FirmYearData[],
): Decoded {
  let acc: Decoded = { year };
  for (const row of fieldRows) {
    const agg = row.agg ?? defaultAgg(row.type);
    if (agg !== "sum") continue;
    const cell = aggregateCell(row, perFirm);
    if (cell.kind !== "sum" && cell.kind !== "mixed") continue;
    if (cell.total === null) continue; // "mixed" ganz ohne parsebaren Wert
    acc = row.set(acc, writeBack(row, cell.total));
  }
  return acc;
}

/**
 * Wie die Summe im synthetischen `Decoded` abgelegt wird. Zahl-Felder bekommen die
 * Zahl, `type: "text"` (heute nur `equityEur`) den mit `fmtEur` formatierten String:
 * die Eigenkapital-Summary baut daraus `„…% / <Wert>"` und würde sonst die nackte
 * Zahl („2000000") in ihr Template drucken.
 */
const writeBack = (row: FieldRowDef, total: number): number | string =>
  row.type === "euro" || row.type === "number" ? total : fmtEur(total);
